import { useEffect, useRef, useState } from "react";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import Paragraph from "@editorjs/paragraph";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import CallToAction from "../editorjs/CallToAction";
import ImageBlock from "../editorjs/ImageBlock";
import YoutubeEmbed from "../editorjs/YoutubeEmbed";
import CodeBlock from "../editorjs/CodeBlock";
import ModelBlock from "../editorjs/ModelBlock";
import ClearFormatting from "../editorjs/ClearFormatting";
import GithubGist from "editorjs-github-gist-plugin";
import Delimiter from "@editorjs/delimiter";
import { loadModelViewer } from "../utils/loadModelViewer";
import MonoButton from "./MonoButton";
import {PaperBG} from "../kits";

import ReactMarkdown from "react-markdown";

// Import Generate Markdown SEO JSON 
import getPostBlockingSchema from "../utils/getPostBlockingSchema";
import useDynamicMeta from "../hooks/useDynamicMeta";

// 🧠 Slug Utility
const generateSlug = (input = "") =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, "-")
    .replace(/(^-|-$)/g, "");

// 🧹 Content Cleanup
function cleanEditorJsBlocks(data) {
  const cleanText = (text) => {
    if (typeof text !== "string") return "";

    // Konvertiere Links von HTML zu Markdown BEVOR wir HTML entfernen
    let cleaned = text
      // <a href="url">text</a> → [text](url)
      .replace(/<a\s+href="([^"]+)"[^>]*>(.*?)<\/a>/gi, "[$2]($1)")
      // <b> und <strong> → **text**
      .replace(/<(b|strong)>(.*?)<\/(b|strong)>/gi, "**$2**")
      // <i> und <em> → *text*
      .replace(/<(i|em)>(.*?)<\/(i|em)>/gi, "*$2*")
      // <code> → `text`
      .replace(/<code>(.*?)<\/code>/gi, "`$1`");

    // Jetzt alle übrigen HTML-Tags entfernen
    cleaned = cleaned
      .replace(/<[^>]+>/g, "")
      .replace(/&gt;/g, ">")
      .replace(/&lt;/g, "<")
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&mdash;/g, "—")
      .replace(/&ndash;/g, "–");

    return cleaned;
  };

  return data.map((block) => {
    const cleaned = { ...block };
    if (cleaned.data?.text) cleaned.data.text = cleanText(cleaned.data.text);
    if (Array.isArray(cleaned.data?.items)) {
      cleaned.data.items = cleaned.data.items.map((item) =>
        typeof item === "string"
          ? cleanText(item)
          : item?.content
          ? { ...item, content: cleanText(item.content) }
          : item
      );
    }
    return cleaned;
  });
}

// 🧠 Plaintext & Markdown Generator
function getPlainTextAndMarkdown(data) {
  const blocks = data?.blocks || [];
  const plainArr = [];
  const mdArr = [];

  blocks.forEach(({ type, data }) => {
    let txt = "";
    let md = "";

    switch (type) {
      case "header":
        txt = data.text || "";
        const headerLevel = data.level || 2;
        const headerPrefix = "#".repeat(headerLevel);
        md = `${headerPrefix} ${data.text || ""}`;
        break;
      case "paragraph":
        txt = data.text || "";
        md = data.text || "";
        break;
      case "quote":
        txt = data.text || "";
        md = `> ${data.text || ""}`;
        break;
      case "list":
        const items = data.items || [];
        const flat = items.map((i) =>
          typeof i === "string" ? i : i.content || ""
        );
        txt = flat.join("\n");
        md = flat.map((i) => `- ${i}`).join("\n");
        break;
      case "code":
        txt = data.content || "";
        md = `\`\`\`${data.language || ""}\n${data.content || ""}\n\`\`\``;
        break;
      case "delimiter":
        txt = "---";
        md = "---";
        break;
      case "image":
        const alt = data.alt?.trim() || data.caption?.trim() || "Bild";
        const url = data.file?.url || data.url || data.src || "";
        if (url) {
          txt = `[Bild: ${alt}] ${url}`;
          md = `![${alt}](${url})`;
        }
        break;
      case "youtube":
        txt = data.url ? `[YouTube: ${data.url}]` : "";
        md = data.url ? `🔗 ${data.url}` : "";
        break;
      case "gist":
        txt = data.url ? `[GitHub Gist: ${data.url}]` : "";
        md = data.url ? `🔗 ${data.url}` : "";
        break;
      case "cta":
        txt = data.content || "";
        md = `**${data.content || ""}**`;
        break;
      case "model3d":
        txt = data.title || "3D-Modell";
        md = `📦 ${txt}`;
        break;
      default:
        break;
    }

    if (txt) plainArr.push(txt);
    if (md) mdArr.push(md);
  });

  return {
    plain: plainArr.join("\n\n"),
    markdown: mdArr.join("\n\n"),
  };
}

// 🔽 File Download Utility
function downloadFile(data, type, filename) {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* ===========================
   Markdown → EditorJS Blocks
   =========================== */
function mdToEditorBlocks(md) {
  const lines = md.split("\n");
  const blocks = [];
  let listBuffer = [];

  const flushList = () => {
    if (listBuffer.length > 0) {
      blocks.push({
        type: "list",
        data: { style: "unordered", items: [...listBuffer] },
      });
      listBuffer = [];
    }
  };

  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      const text = paragraphBuffer.join("\n").trim();
      if (text) {
        blocks.push({
          type: "paragraph",
          data: { text },
        });
      }
      paragraphBuffer = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].replace(/\r$/, "");

    // Header
    if (/^#{1,6}\s+/.test(line)) {
      flushList();
      flushParagraph();
      const level = Math.min(6, line.match(/^#{1,6}/)[0].length);
      const text = line.replace(/^#{1,6}\s+/, "");
      // EditorJS unterstützt Level 2-4, daher mappen wir: # → 2, ## → 2, ### → 3, etc.
      const headerLevel = level === 1 ? 2 : Math.min(4, Math.max(2, level));
      blocks.push({
        type: "header",
        data: { level: headerLevel, text },
      });
      i++;
      continue;
    }

    // Quote
    if (/^>\s+/.test(line)) {
      flushList();
      flushParagraph();
      blocks.push({
        type: "quote",
        data: { text: line.replace(/^>\s+/, "") },
      });
      i++;
      continue;
    }

    // List
    if (/^-\s+/.test(line) || /^\*\s+/.test(line) || /^\d+\.\s+/.test(line)) {
      const item = line.replace(/^(-|\*|\d+\.)\s+/, "");
      listBuffer.push(item);
      i++;
      continue;
    }

    // Image
    const imageMatch = line.match(/!\[(.*?)\]\((.*?)\)/);
    if (imageMatch) {
      flushList();
      flushParagraph();
      blocks.push({
        type: "image",
        data: {
          caption: imageMatch[1] || "",
          file: { url: imageMatch[2] || "" },
        },
      });
      i++;
      continue;
    }

    // Code Block - robuste Logik für auch kaputte Markdown-Dateien
    const codeFenceMatch = line.match(/^```([\w-]*)/);
    if (codeFenceMatch) {
      flushList();
      flushParagraph();

      const lang = codeFenceMatch[1] || "text";
      const codeLines = [];
      i++; // Skip opening fence

      // Sammle alle Zeilen bis zum schließenden ``` (auch mit beliebigem Inhalt nach ```)
      while (i < lines.length && !lines[i].match(/^```/)) {
        codeLines.push(lines[i]);
        i++;
      }

      // Skip closing fence (egal ob ```text oder nur ```)
      if (i < lines.length && lines[i].match(/^```/)) {
        i++;
      }

      blocks.push({
        type: "code",
        data: {
          language: lang,
          content: codeLines.join("\n"),
        },
      });
      continue;
    }

    // Delimiter (---, ***, ___)
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushList();
      flushParagraph();
      blocks.push({
        type: "delimiter",
        data: {},
      });
      i++;
      continue;
    }

    // Blank line
    if (line.trim() === "") {
      flushList();
      flushParagraph();
      i++;
      continue;
    }

    // Default: paragraph
    paragraphBuffer.push(line);
    i++;
  }

  flushList();
  flushParagraph();

  return blocks;
}

/* ===========================
   Main Component
   =========================== */
export default function SmartZenEditor() {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [date, setDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [image, setImage] = useState("");
  const [editorData, setEditorData] = useState(null);
  const [availableArticles, setAvailableArticles] = useState([]);
  const [markdown, setMarkdown] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isMarkdownMode, setIsMarkdownMode] = useState(false);
  const ejInstance = useRef(null);
  const slug = generateSlug(title);

  const mdUploadRef = useRef(null);

  useDynamicMeta({
    title,
    description: subtitle,
    image,
    schemaJson: getPostBlockingSchema({
      title,
      subtitle,
      date,
      image,
      markdown,
      slug,
    }),
  });

  // Load article list
  useEffect(() => {
    fetch("https://www.denisbitter.de/stage01/api/zens.php")
      .then((res) => res.json())
      .then(setAvailableArticles)
      .catch(console.error);
  }, []);

  // Init EditorJS
  useEffect(() => {
    loadModelViewer();

    const timeout = setTimeout(() => {
      ejInstance.current = new EditorJS({
        holder: "editorjs",
        autofocus: true,
        tools: {
          delimiter: Delimiter,
          header: {
            class: Header,
            inlineToolbar: ["link", "bold", "italic", "clearFormatting"],
            config: { levels: [2, 3, 4], defaultLevel: 2 },
          },
          paragraph: {
            class: Paragraph,
            inlineToolbar: ["link", "bold", "italic", "clearFormatting"],
          },
          list: List,
          quote: Quote,
          cta: CallToAction,
          image: ImageBlock,
          youtube: YoutubeEmbed,
          code: CodeBlock,
          model3d: { class: ModelBlock, toolbox: ModelBlock.toolbox },
          clearFormatting: ClearFormatting,
          gist: {
            class: GithubGist,
            config: {
              endpoint: "https://api.github.com/gists",
            },
          },
        },
        onReady: () => console.log("✅ Editor.js ready"),
      });
    }, 0);

    return () => {
      clearTimeout(timeout);
      if (ejInstance.current && typeof ejInstance.current.destroy === "function") {
        ejInstance.current.destroy();
        ejInstance.current = null;
      }
    };
  }, []);

  // Save handler (unterstützt beide Modi)
  const handleSave = async () => {
    try {
      let cleaned;

      if (isMarkdownMode) {
        // Im Markdown-Modus: Markdown zu Blocks konvertieren
        const blocks = mdToEditorBlocks(markdown);
        cleaned = cleanEditorJsBlocks(blocks);
        setEditorData({ blocks: cleaned });

        // Markdown neu generieren aus den Blocks (für konsistente Formatierung)
        const { markdown: md } = getPlainTextAndMarkdown({ blocks: cleaned });
        setMarkdown(md);

        // Editor aktualisieren für späteren Wechsel
        if (ejInstance.current && typeof ejInstance.current.render === "function") {
          ejInstance.current.render({ blocks: cleaned });
        }
      } else {
        // Im EditorJS-Modus: Normale Speicherung
        const output = await ejInstance.current.save();
        cleaned = cleanEditorJsBlocks(output.blocks);
        setEditorData({ blocks: cleaned });

        const { markdown: md } = getPlainTextAndMarkdown({ blocks: cleaned });
        setMarkdown(md);
      }

      // Markdown generieren (falls noch nicht vorhanden)
      const finalMarkdown = markdown || getPlainTextAndMarkdown({ blocks: cleaned }).markdown;

      await fetch("https://www.denisbitter.de/stage01/api/save_zens.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug,
          title,
          subtitle,
          date,
          image: image || `https://www.denisbitter.de/images/${slug}.jpg`,
          blocks: cleaned,
          markdown: finalMarkdown, // Markdown hinzufügen
        }),
      });
      alert("✅ Artikel gespeichert!");
    } catch (err) {
      console.error(err);
      alert("Fehler beim Speichern. Schau in die Konsole.");
    }
  };

  const downloadPlain = () => {
    const plain = getPlainTextAndMarkdown(editorData).plain;
    downloadFile(plain, "text/plain", `${slug || "post"}-klartext.txt`);
  };

  const downloadMd = () => {
    downloadFile(markdown, "text/markdown", `${slug || "post"}-post.md`);
  };

  const downloadJson = () => {
    if (!editorData) {
      alert("Keine Editor-Daten zum Herunterladen vorhanden.");
      return;
    }
    const jsonData = {
      title,
      subtitle,
      date,
      image: image || `https://www.denisbitter.de/images/${slug}.jpg`,
      slug,
      blocks: editorData.blocks,
      markdown,
    };
    downloadFile(
      JSON.stringify(jsonData, null, 2),
      "application/json",
      `${slug || "post"}-editorjs.json`
    );
  };

  // Load JSON article
  const loadJson = async (slugValue) => {
    if (!slugValue) return;
    const res = await fetch(
      `https://www.denisbitter.de/stage01/api/zens.php?slug=${slugValue}`
    );
    const json = await res.json();
    const raw = json.blocks || json.content?.blocks || [];
    const blocks = raw.map((b) =>
      b.type === "model" ? { ...b, type: "model3d" } : b
    );
    if (ejInstance.current && typeof ejInstance.current.render === "function") {
      ejInstance.current.render({ blocks });
    }
    setEditorData({ blocks });

    const { markdown: md } = getPlainTextAndMarkdown({ blocks });
    setMarkdown(md);

    setTitle(json.title || "");
    setSubtitle(json.subtitle || "");
    setDate(json.date || new Date().toISOString().slice(0, 10));
    setImage(json.image || "");
  };
  
// Refresh Reload Button 

const refreshArticleList = () => {
  fetch("https://www.denisbitter.de/stage01/api/zens.php")
    .then((res) => res.json())
    .then(setAvailableArticles)
    .catch(console.error);
};
// 🔥 DELETE Article Handler
const handleDelete = async () => {
  if (!slug) return alert("Kein Artikel zum Löschen ausgewählt.");
  setShowDeleteConfirm(true);
};

const confirmDelete = async () => {
  setShowDeleteConfirm(false);

  try {
    const res = await fetch(
      `https://www.denisbitter.de/stage01/api/delete_zens.php?slug=${encodeURIComponent(slug)}`,
      {
        method: "DELETE",
      }
    );

    const json = await res.json();

    if (json.success) {
      alert(`✅ Artikel "${title}" gelöscht!`);
      setTitle("");
      setSubtitle("");
      setDate(new Date().toISOString().slice(0, 10));
      setImage("");
      setEditorData(null);
      setMarkdown("");
      refreshArticleList();
    } else {
      alert("Fehler beim Löschen: " + (json.message || "Unbekannt"));
    }
  } catch (err) {
    console.error(err);
    alert("Fehler beim Löschen des Artikels.");
  }
};


const cancelDelete = () => {
  setShowDeleteConfirm(false);
};

  // Toggle zwischen Markdown und EditorJS
  const toggleMarkdownMode = async () => {
    if (!isMarkdownMode) {
      // Wechsel zu Markdown: EditorJS Daten holen
      try {
        const output = await ejInstance.current.save();
        const cleaned = cleanEditorJsBlocks(output.blocks);
        const { markdown: md } = getPlainTextAndMarkdown({ blocks: cleaned });
        setMarkdown(md);
        setEditorData({ blocks: cleaned });
      } catch (err) {
        console.error("Fehler beim Speichern:", err);
      }
    } else {
      // Wechsel zu EditorJS: Markdown zu Blocks konvertieren
      const blocks = mdToEditorBlocks(markdown);
      const cleaned = cleanEditorJsBlocks(blocks);
      if (ejInstance.current && typeof ejInstance.current.render === "function") {
        ejInstance.current.render({ blocks: cleaned });
      }
      setEditorData({ blocks: cleaned });
    }
    setIsMarkdownMode(!isMarkdownMode);
  };

  // Markdown Toolbar Helper Functions
  const textareaRef = useRef(null);

  const insertMarkdown = (before, after = "", placeholder = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = markdown.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newText =
      markdown.substring(0, start) +
      before + textToInsert + after +
      markdown.substring(end);

    setMarkdown(newText);

    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + textToInsert.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertHeading = (level) => {
    const prefix = "#".repeat(level) + " ";
    insertMarkdown(prefix, "", "Überschrift");
  };

  const insertBold = () => insertMarkdown("**", "**", "fetter Text");
  const insertItalic = () => insertMarkdown("*", "*", "kursiver Text");
  const insertCode = () => insertMarkdown("`", "`", "code");
  const insertCodeBlock = () => insertMarkdown("```\n", "\n```", "code hier");
  const insertLink = () => insertMarkdown("[", "](url)", "Link-Text");
  const insertImage = () => insertMarkdown("![", "](url)", "Bild-Beschreibung");
  const insertList = () => insertMarkdown("- ", "", "Listeneintrag");
  const insertNumberedList = () => insertMarkdown("1. ", "", "Listeneintrag");
  const insertQuote = () => insertMarkdown("> ", "", "Zitat");
  const insertDivider = () => insertMarkdown("\n---\n", "", "");

  /* =======================================
     Handle File Upload (Markdown OR JSON)
     - detects file type by extension
     - JSON: loads EditorJS blocks directly
     - Markdown: converts to blocks
     - saves to server
     ======================================= */
  const handleFileUploadAndSave = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      const fileName = file.name.toLowerCase();
      const isJSON = fileName.endsWith('.json');
      const isMarkdown = fileName.endsWith('.md') || fileName.endsWith('.markdown');

      if (!isJSON && !isMarkdown) {
        alert("Bitte nur .md oder .json Dateien hochladen!");
        return;
      }

      const text = await file.text();
      let cleanedBlocks;
      let newTitle = "";
      let newSubtitle = "";
      let imgUrl = "";

      if (isJSON) {
        // JSON-Datei: EditorJS Format direkt laden
        const jsonData = JSON.parse(text);

        // Unterstütze verschiedene JSON-Strukturen
        const rawBlocks = jsonData.blocks || jsonData.content?.blocks || [];

        // Model → model3d Kompatibilität
        const blocks = rawBlocks.map((b) =>
          b.type === "model" ? { ...b, type: "model3d" } : b
        );

        cleanedBlocks = cleanEditorJsBlocks(blocks);

        // Metadaten aus JSON extrahieren (falls vorhanden)
        newTitle = jsonData.title || "Neuer Zen aus JSON";
        newSubtitle = jsonData.subtitle || "";
        imgUrl = jsonData.image || "";

        // Markdown aus Blöcken generieren
        const { markdown: md } = getPlainTextAndMarkdown({ blocks: cleanedBlocks });
        setMarkdown(md);

      } else {
        // Markdown-Datei: Konvertieren
        setMarkdown(text);

        const rawBlocks = mdToEditorBlocks(text);
        cleanedBlocks = cleanEditorJsBlocks(rawBlocks);

        // Titel aus Markdown extrahieren
        const h1 = text.match(/^#\s+(.*)/m);
        const h2 = text.match(/^##\s+(.*)/m);
        if (h1) newTitle = h1[1].trim();
        else if (h2) newTitle = h2[1].trim();
        else {
          const firstNonEmpty = text.split("\n").find((l) => l.trim() !== "");
          newTitle = (firstNonEmpty || "Neuer Zen").replace(/^#+\s*/, "").slice(0, 120);
        }

        // Subtitle aus Frontmatter
        const subtitleMatch = text.match(/^-{3}[\s\S]*?subtitle:\s*(.*)/m);
        if (subtitleMatch) newSubtitle = subtitleMatch[1].trim();

        // Bild aus Markdown
        const imgMatch = text.match(/!\[.*?\]\((.*?)\)/);
        imgUrl = imgMatch?.[1] || "";
      }

      // Render in Editor
      if (ejInstance.current && typeof ejInstance.current.render === "function") {
        ejInstance.current.render({ blocks: cleanedBlocks });
      }
      setEditorData({ blocks: cleanedBlocks });

      // Metadaten setzen
      setTitle(newTitle);
      setSubtitle(newSubtitle);

      const newSlug = generateSlug(newTitle);
      const finalImgUrl = imgUrl || `https://www.denisbitter.de/images/${newSlug}.jpg`;
      setImage(finalImgUrl);

      const newDate = new Date().toISOString().slice(0, 10);
      setDate(newDate);

      // Speichern
      await fetch("https://www.denisbitter.de/stage01/api/save_zens.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: newSlug,
          title: newTitle,
          subtitle: newSubtitle || "",
          date: newDate,
          image: finalImgUrl,
          blocks: cleanedBlocks,
        }),
      });

      alert(`🚀 ${isJSON ? 'JSON' : 'Markdown'} geladen & als ZenPost gespeichert!`);
      e.target.value = "";
    } catch (err) {
      console.error(err);
      alert("Fehler beim Importieren der Datei. Schau in die Konsole.");
    }
  };

  return (
    

      
      <div className="grid md:grid-cols-2 gap-6 p-6 bg-[#dfdfdf]">
      {/* LEFT: Editor & Save */}
      <div className="space-y-4">
        <h2 className="text-xs">Zen SmartEditor</h2>

        <MonoButton
          className="text-xs w-1/2"
          onClick={() => mdUploadRef.current && mdUploadRef.current.click()}
        >
          📤 MD/JSON laden & speichern
        </MonoButton>


        <MonoButton
          className="text-xs bg-red-500 w-1/2 hover:bg-red-600"
          onClick={handleDelete}
        >
          🗑️ Artikel löschen
        </MonoButton>

        <MonoButton className="text-xs w-full" onClick={refreshArticleList}>
          🔄 Refresh
        </MonoButton>

        <div className="flex items-center gap-2 mb-2">
          <select
            onChange={(e) => e.target.value && loadJson(e.target.value)}
            className="border p-1 w-full text-xs"
          >
            <option value="">Zen wählen…</option>
            {availableArticles.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>


        </div>




        <input
          placeholder="Titel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border p-1 w-full mb-1 text-xs" />
        <input
          placeholder="Untertitel"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          className="border p-1 w-full mb-1 text-xs" />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border p-1 w-full mb-1 text-xs" />
        <input
          placeholder="Bild-URL"
          value={image}
          onChange={(e) => setImage(e.target.value)}
          className="border p-1 w-full mb-2 text-xs" />

        {/* Toggle Button */}
        <MonoButton
          className="text-xs w-full mb-2"
          onClick={toggleMarkdownMode}
        >
          {isMarkdownMode ? "📝 Zu EditorJS wechseln" : "✍️ Zu Markdown wechseln"}
        </MonoButton>

        {/* Markdown Toolbar - nur sichtbar im Markdown-Modus */}
        {isMarkdownMode && (
          <div className="border rounded p-2 mb-2 bg-gray-50 flex flex-wrap gap-1">
            <button
              onClick={() => insertHeading(2)}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Überschrift H2"
            >
              H2
            </button>
            <button
              onClick={() => insertHeading(3)}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Überschrift H3"
            >
              H3
            </button>
            <button
              onClick={insertBold}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100 font-bold"
              title="Fett"
            >
              B
            </button>
            <button
              onClick={insertItalic}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100 italic"
              title="Kursiv"
            >
              I
            </button>
            <button
              onClick={insertCode}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100 font-mono"
              title="Inline Code"
            >
              {'<>'}
            </button>
            <button
              onClick={insertCodeBlock}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Code Block"
            >
              {'{ }'}
            </button>
            <button
              onClick={insertLink}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Link"
            >
              🔗
            </button>
            <button
              onClick={insertImage}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Bild"
            >
              🖼️
            </button>
            <button
              onClick={insertList}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Liste"
            >
              ●
            </button>
            <button
              onClick={insertNumberedList}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Nummerierte Liste"
            >
              1.
            </button>
            <button
              onClick={insertQuote}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Zitat"
            >
              "
            </button>
            <button
              onClick={insertDivider}
              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
              title="Trennlinie"
            >
              ―
            </button>
          </div>
        )}

        {/* Editor und Markdown Textarea - beide im DOM, aber nur eines sichtbar */}
        <textarea
          ref={textareaRef}
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
          className={`w-full min-h-[300px] border p-2 text-xs font-mono ${isMarkdownMode ? '' : 'hidden'}`}
          placeholder="Schreibe dein Markdown hier..." />
        <div
          id="editorjs"
          className={`min-h-[300px] border mb-2 text-xs ${isMarkdownMode ? 'hidden' : ''}`}
        ></div>

        <div className="flex gap-2">
          <MonoButton className="text-xs" onClick={handleSave}>
            💾 Save & Generate Markdown
          </MonoButton>


          <input
            type="file"
            accept=".md,.markdown,.json,text/markdown,application/json"
            ref={mdUploadRef}
            onChange={handleFileUploadAndSave}
            className="hidden" />
        </div>
      </div>

      {/* RIGHT: Preview & Downloads */}
      <div>
        <h2 className="text-xs">📄 Live Markdown Preview</h2>
        <div className="border p-4 mb-2 max-h-[500px] overflow-auto text-xs prose prose-sm max-w-none">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-4 mb-2" {...props} />,
              h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-3 mb-2" {...props} />,
              h3: ({ node, ...props }) => <h3 className="text-lg font-bold mt-2 mb-1" {...props} />,
              p: ({ node, ...props }) => <p className="mb-2" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc ml-4 mb-2" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal ml-4 mb-2" {...props} />,
              li: ({ node, ...props }) => <li className="mb-1" {...props} />,
              blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-400 pl-4 italic my-2" {...props} />,
              code: ({ node, inline, ...props }) => inline
                ? <code className="bg-gray-100 px-1 rounded" {...props} />
                : <code className="block bg-gray-100 p-2 rounded my-2 overflow-x-auto" {...props} />,
              pre: ({ node, ...props }) => <pre className="bg-gray-100 p-2 rounded my-2 overflow-x-auto" {...props} />,
              img: ({ node, ...props }) => <img className="max-w-full h-auto my-2" {...props} />,
              hr: ({ node, ...props }) => <hr className="my-4 border-t border-gray-300" {...props} />,
            }}
          >
            {markdown}
          </ReactMarkdown>
        </div>

        {showDeleteConfirm && (
          <div className="border border-red-500 bg-red-50 p-3 mb-2 text-xs rounded">
            <p className="mb-2">Willst du den Artikel "{title}" wirklich löschen?</p>
            <div className="flex gap-2">
              <MonoButton
                className="text-xs bg-red-500 hover:bg-red-600"
                onClick={confirmDelete}
              >
                Ja, löschen
              </MonoButton>
              <MonoButton
                className="text-xs bg-gray-400 hover:bg-gray-500"
                onClick={cancelDelete}
              >
                Abbrechen
              </MonoButton>
            </div>
          </div>
        )}




        <div className="flex flex-wrap gap-2 mb-4">
          <MonoButton onClick={downloadPlain} className="text-xs">
            📥 Klartext
          </MonoButton>
          <MonoButton className="text-xs" onClick={downloadMd}>
            📥 Markdown
          </MonoButton>
          <MonoButton className="text-xs" onClick={downloadJson}>
            📥 JSON
          </MonoButton>
        </div>

        <h4 className="mt-4 text-xs">🔍 JSON Debug Output</h4>
        <pre className="border p-2 max-h-[200px] overflow-auto text-xs">
          {editorData
            ? JSON.stringify(cleanEditorJsBlocks(editorData.blocks), null, 2)
            : "// Noch nichts geladen"}
        </pre>
      </div>
    </div>

   
  );
}
